/**
 * Video Story — brief → storyboard → a shot per scene → a clip per shot →
 * an assembled timeline.
 *
 * The storyboard is turned into an ordinary planner plan (one `add_recipe`
 * per scene plus a chained `image-to-video` per scene), so shooting goes
 * through exactly the machinery a plan from the agent uses: the headless 3D
 * capture queue for room scenes, chained nodes waiting for their parent's
 * picture, the canvas invariant. What lives here is pure: the mapping in,
 * the progress read back from the canvas, and the scenes handed to
 * `buildCompositionFromStoryboard` once everything has landed.
 */
import type { CsAsset } from '../../../../common/models/content-studio.model';
import type {
    CanvasGenerationNode,
    CanvasNode,
    CanvasRecipeNode,
} from '../canvas/canvas-graph.types';
import type { ContentPlan, PlanOperation } from '../planner/plan-compiler';
import type { AspectRatio } from './composition';
import type { StoryboardScene } from './templates';

// ─── the API's storyboard ─────────────────────────────────────────────

export type StoryShot = 'detail' | 'room' | 'product';

export interface StorySceneMotion {
    cameraMove: string;
    lightingTransition: string;
    intensity: string;
}

export interface StorySceneSpec {
    id: string;
    productUuid: string;
    shot: StoryShot;
    description: string;
    recipeSlug: string;
    imageInputs: Record<string, any>;
    motion: StorySceneMotion;
    durationSec: number;
    caption?: { text: string; preset?: string };
    transition?: string;
}

export interface Storyboard {
    title: string;
    aspect: AspectRatio;
    totalSec: number;
    concept?: string;
    music?: { prompt: string; durationSec: number };
    captionPreset?: string;
    scenes: StorySceneSpec[];
    endCard?: { tagline?: string; url?: string };
}

export interface StoryboardRequest {
    brief: string;
    productUuids: string[];
    aspect?: AspectRatio;
    targetSec?: number;
    inspirationFrames?: string[];
    language?: string;
}

// ─── what the video-edit node remembers ───────────────────────────────

export interface VideoStorySceneLink {
    sceneId: string;
    /** The recipe node that shoots the still. */
    imageNodeId: string;
    /** The image-to-video node chained off it. */
    videoNodeId: string;
}

export interface VideoStoryState {
    /** writing: the storyboard is being written (planner flow); running: shooting/animating. */
    phase: 'writing' | 'running' | 'failed';
    request?: StoryboardRequest;
    storyboard?: Storyboard;
    scenes: VideoStorySceneLink[];
    music?: { status: 'pending' | 'ready' | 'failed' | 'skipped'; url?: string; durationSec?: number };
    error?: string;
    startedAt: string;
    /** Set once the timeline has been built from the clips. */
    assembledAt?: string;
}

// ─── storyboard → plan ────────────────────────────────────────────────

/** The image-to-video step for a scene: preset camera move, 5 s, 720p. */
export function videoInputsFor(scene: StorySceneSpec, aspect: AspectRatio): Record<string, any> {
    return {
        mode: 'preset',
        cameraMove: scene.motion?.cameraMove || 'push-in',
        lightingTransition: scene.motion?.lightingTransition || 'none',
        intensity: scene.motion?.intensity || 'subtle',
        duration: '5',
        aspectRatio: aspect,
        resolution: '720p',
    };
}

export const videoStepId = (sceneId: string) => `${sceneId}__video`;

/**
 * A storyboard as a planner plan: each scene shoots a still off its product,
 * and a video step chained to that still animates it.
 */
export function storyboardToPlan(sb: Storyboard): ContentPlan {
    const aspect = sb.aspect || '9:16';
    const operations: PlanOperation[] = [];
    for (const scene of sb.scenes) {
        operations.push({
            op: 'add_recipe',
            id: scene.id,
            recipeSlug: scene.recipeSlug,
            productUuid: scene.productUuid,
            inputs: { ...(scene.imageInputs || {}), aspectRatio: scene.imageInputs?.aspectRatio || aspect },
            rationale: scene.description,
        });
    }
    for (const scene of sb.scenes) {
        operations.push({
            op: 'add_recipe',
            id: videoStepId(scene.id),
            recipeSlug: 'image-to-video',
            sourceOpId: scene.id,
            inputs: videoInputsFor(scene, aspect),
        });
    }
    return {
        summary: sb.title,
        productUuids: Array.from(new Set(sb.scenes.map((s) => s.productUuid).filter(Boolean))),
        operations,
    };
}

/** Which canvas nodes a compiled storyboard produced for each scene. */
export function linkScenes(
    sb: Storyboard,
    steps: Array<{ step: { id?: string }; node: { id: string } }>,
): VideoStorySceneLink[] {
    const byStep = new Map(steps.map((s) => [s.step.id, s.node.id]));
    const out: VideoStorySceneLink[] = [];
    for (const scene of sb.scenes) {
        const imageNodeId = byStep.get(scene.id);
        const videoNodeId = byStep.get(videoStepId(scene.id));
        if (imageNodeId && videoNodeId) out.push({ sceneId: scene.id, imageNodeId, videoNodeId });
    }
    return out;
}

// ─── progress ─────────────────────────────────────────────────────────

export interface SceneProgress {
    sceneId: string;
    image?: CsAsset;
    video?: CsAsset;
    imageFailed: boolean;
    videoFailed: boolean;
    /** Nothing more will happen for this scene. */
    settled: boolean;
}

export interface StoryProgress {
    phase: 'writing' | 'shooting' | 'animating' | 'assembling' | 'ready' | 'failed';
    total: number;
    shot: number;
    animated: number;
    failed: number;
    scenes: SceneProgress[];
    /** Every scene has settled and the music is no longer pending. */
    readyToAssemble: boolean;
}

function gensOf(nodes: CanvasNode[], recipeNodeId: string): CanvasGenerationNode[] {
    return nodes.filter((n) => n.kind === 'generation'
        && (n as CanvasGenerationNode).recipeNodeId === recipeNodeId) as CanvasGenerationNode[];
}

function newestReady(gens: CanvasGenerationNode[]): CsAsset | undefined {
    return [...gens].reverse().find((g) => g.asset?.status === 'ready' && g.asset.url)?.asset;
}

/** A step is dead when everything it made failed and it is not running again. */
function stepFailed(nodes: CanvasNode[], recipeNodeId: string, gens: CanvasGenerationNode[]): boolean {
    const rn = nodes.find((n) => n.id === recipeNodeId) as CanvasRecipeNode | undefined;
    if (!rn) return true;
    return gens.length > 0 && gens.every((g) => g.asset?.status === 'failed') && !rn.generating;
}

export function storyProgress(story: VideoStoryState | undefined, nodes: CanvasNode[]): StoryProgress {
    const empty: StoryProgress = { phase: 'writing', total: 0, shot: 0, animated: 0, failed: 0, scenes: [], readyToAssemble: false };
    if (!story) return { ...empty, phase: 'ready' };
    if (story.phase === 'failed') return { ...empty, phase: 'failed' };
    if (story.phase === 'writing' || !story.storyboard) return empty;

    const scenes: SceneProgress[] = story.scenes.map((link) => {
        const imageGens = gensOf(nodes, link.imageNodeId);
        const videoGens = gensOf(nodes, link.videoNodeId);
        const image = newestReady(imageGens);
        const video = newestReady(videoGens);
        const imageFailed = !image && stepFailed(nodes, link.imageNodeId, imageGens);
        // A video whose still failed will never start: its parent gave up.
        const videoFailed = !video && (imageFailed || stepFailed(nodes, link.videoNodeId, videoGens));
        return { sceneId: link.sceneId, image, video, imageFailed, videoFailed, settled: !!video || videoFailed };
    });
    const total = scenes.length;
    const shot = scenes.filter((s) => s.image).length;
    const animated = scenes.filter((s) => s.video).length;
    const failed = scenes.filter((s) => s.videoFailed).length;
    const musicPending = story.music?.status === 'pending';
    const allSettled = scenes.every((s) => s.settled);
    const readyToAssemble = total > 0 && allSettled && !musicPending;
    let phase: StoryProgress['phase'];
    if (story.assembledAt) phase = 'ready';
    else if (readyToAssemble || allSettled) phase = 'assembling';
    else if (shot + scenes.filter((s) => s.imageFailed).length < total) phase = 'shooting';
    else phase = 'animating';
    return { phase, total, shot, animated, failed, scenes, readyToAssemble };
}

// ─── assembly ─────────────────────────────────────────────────────────

/** Seconds skipped at the head of each generated clip (models settle in the first frames). */
export const CLIP_HEAD_TRIM = 0.5;
const DEFAULT_CLIP_SECONDS = 5;

/**
 * The scenes to cut: the clip where there is one, the still (with a gentle
 * Ken Burns) where only the video failed, nothing where the still failed too.
 */
export function storyScenesForAssembly(sb: Storyboard, progress: StoryProgress): StoryboardScene[] {
    const byId = new Map(progress.scenes.map((s) => [s.sceneId, s]));
    const out: StoryboardScene[] = [];
    for (const scene of sb.scenes) {
        const p = byId.get(scene.id);
        if (!p) continue;
        const duration = Math.max(0.6, Number(scene.durationSec) || 2);
        const caption = scene.caption?.text?.trim()
            ? { text: scene.caption.text, preset: scene.caption.preset || sb.captionPreset }
            : undefined;
        if (p.video?.url) {
            const source = p.video.dimensions?.durationSec || DEFAULT_CLIP_SECONDS;
            const trimIn = Math.max(0, Math.min(CLIP_HEAD_TRIM, source - duration));
            out.push({
                src: p.video.url,
                kind: 'video',
                assetUuid: p.video.uuid,
                thumbnailUrl: p.video.thumbnailUrl || p.image?.url,
                durationSec: Math.min(duration, source),
                trimInSec: trimIn,
                sourceDurationSec: source,
                caption,
                transition: scene.transition,
            });
        } else if (p.image?.url) {
            out.push({
                src: p.image.url,
                kind: 'image',
                assetUuid: p.image.uuid,
                durationSec: duration,
                caption,
                transition: scene.transition,
            });
        }
    }
    return out;
}

/** Only a real domain belongs on an end card ("social" is what discovery sometimes records). */
export function cleanWebsite(value?: string): string | undefined {
    if (!value) return undefined;
    try {
        const host = new URL(value.startsWith('http') ? value : `https://${value}`).hostname.replace(/^www\./, '');
        return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(host) ? host : undefined;
    } catch {
        return undefined;
    }
}

/** Total seconds of a storyboard (scenes only; the end card is added on top). */
export function storyboardSeconds(sb: Storyboard): number {
    return Math.round(sb.scenes.reduce((t, s) => t + (Number(s.durationSec) || 0), 0) * 10) / 10;
}

/** A fresh scene id that does not collide with the board's. */
export function nextSceneId(sb: Storyboard): string {
    const used = new Set(sb.scenes.map((s) => s.id));
    let i = sb.scenes.length + 1;
    while (used.has(`s${i}`)) i++;
    return `s${i}`;
}

/** Read the API's answer, whichever envelope it came in. */
export function readStoryboard(raw: any): Storyboard {
    const sb = raw?.storyboard ?? raw?.data?.storyboard ?? raw?.data ?? raw;
    if (!sb || !Array.isArray(sb.scenes)) throw new Error('The storyboard came back empty');
    return sb as Storyboard;
}
